/**
 * CartForm - Custom element for main cart page that handles AJAX updates
 * Listens to cart:change events and updates the cart content without page reload
 */
class CartForm extends HTMLElement {
    constructor() {
        super();
        this.sectionId = this.getAttribute('section-id');
        this.onBundleSection = this.onBundleSection.bind(this);
        this.onCartChange = this.onCartChange.bind(this);
    }

    connectedCallback() {
        document.addEventListener('cart:prepare-bundled-sections', this.onBundleSection);
        document.addEventListener('cart:change', this.onCartChange);
    }

    disconnectedCallback() {
        document.removeEventListener('cart:prepare-bundled-sections', this.onBundleSection);
        document.removeEventListener('cart:change', this.onCartChange);
    }

    onBundleSection(event) {
        // Register this section to be included in the bundled sections request
        const sectionId = `main-cart-items-${this.sectionId}`;
        event.detail.sections.push(sectionId);
        // Also push the full section id
        event.detail.sections.push(this.sectionId);
    }

    async onCartChange(event) {
        const cartData = event.detail.cart;
        
        // Check if we have section rendering data
        if (cartData.sections && cartData.sections[this.sectionId]) {
            this.replaceContent(cartData.sections[this.sectionId]);
        } else {
            // Fallback: fetch the section directly
            await this.refreshCart();
        }
    }

    async refreshCart() {
        try {
            const response = await fetch(`${window.location.pathname}?section_id=${this.sectionId}`);
            if (response.ok) {
                const html = await response.text();
                this.replaceContent(html);
            }
        } catch (error) {
            console.error('Error refreshing cart:', error);
        }
    }

    replaceContent(html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Find the new cart-form content
        const newCartForm = tempDiv.querySelector('cart-form');
        
        if (newCartForm) {
            // Replace the inner content of this element
            this.innerHTML = newCartForm.innerHTML;
        } else {
            // Check if cart is now empty - look for empty-state element
            const emptyState = tempDiv.querySelector('.empty-state');
            if (emptyState) {
                // Cart is empty, replace the entire section content
                const sectionContainer = tempDiv.querySelector('.section-spacing');
                if (sectionContainer) {
                    // Replace the parent section with the empty cart state
                    const currentSection = this.closest('.section-spacing');
                    if (currentSection) {
                        currentSection.replaceWith(sectionContainer);
                        return;
                    }
                }
            }
            
            // Fallback: look for the form directly
            const newForm = tempDiv.querySelector('.cart-page');
            if (newForm) {
                const currentForm = this.querySelector('.cart-page');
                if (currentForm) {
                    currentForm.innerHTML = newForm.innerHTML;
                }
            }
        }
        
        // Dispatch event to notify that cart has been updated
        this.dispatchEvent(new CustomEvent('cart-form:updated', { bubbles: true }));
    }
}

if (!customElements.get('cart-form')) {
    customElements.define('cart-form', CartForm);
}

class VariantSelects extends HTMLElement {
    constructor() {
        super();
        this.addEventListener("change", this.onVariantChange);        
    }

    onVariantChange(event) {
        this.updateOptions();
        this.updateMasterId();
        this.toggleAddButton(true, "", false);
        this.updatePickupAvailability();
        // this.removeErrorMessage();
        this.updateInfoText(event);

        if (!this.currentVariant) {
            this.toggleAddButton(true, "", true);
            this.setUnavailable();
        } else {
            this.updateMedia();
            this.updateURL();
            this.updateVariantInput();
            this.renderProductInfo();
            this.updateShareUrl();
        }
        this.customFormButton();        
    }

    updateOptions() {
        this.options = Array.from(
        this.querySelectorAll("select"),
            (select) => select.value
        );
    }

    updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options
            .map((option, index) => {
            return this.options[index] === option;
            })
            .includes(false);
        });
    }

    updateMedia() {
        if (!this.currentVariant) return;
        if (!this.currentVariant.featured_media) return;
        const newMedia = document.querySelector(
        `.product__media-item[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
        );

        
        if (!newMedia) return;
        window.productSlider.slideToLoop(Number(newMedia.dataset.swiperSlideIndex));

        // if (!newMedia) return;
        // const modalContent = document.querySelector(
        //   `#ProductModal-${this.dataset.section} .product-media-modal__content`
        // );
        // const newMediaModal = modalContent.querySelector(
        //   `[data-media-id="${this.currentVariant.featured_media.id}"]`
        // );
        // const parent = newMedia.parentElement;
        // if (parent.firstChild == newMedia) return;
        // modalContent.prepend(newMediaModal);
        // parent.prepend(newMedia);
        // this.stickyHeader =
        //   this.stickyHeader || document.querySelector("sticky-header");
        // if (this.stickyHeader) {
        //   this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
        // }
        // window.setTimeout(() => {
        //   parent.scrollLeft = 0;
        //   parent
        //     .querySelector("li.product__media-item")
        //     .scrollIntoView({ behavior: "smooth" });
        // });
    }

    updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === "false") return;
        window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.currentVariant.id}`
        );
    }

    updateShareUrl() {
        const shareButton = document.getElementById(
        `Share-${this.dataset.section}`
        );
        if (!shareButton) return;
        shareButton.updateUrl(
        `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
        );
    }

    updateVariantInput() {
        const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment`
        );

        productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        });
    }

    updateInfoText(event) {
        let value = event.target.closest("input:checked").value;
        let inventory = Number(event.target.closest("input:checked").dataset.inventory);

        const selectedValues = document.querySelectorAll(`.product-form__input input[value="${value}"]`);

        selectedValues.forEach(selectedVal => {
            selectedVal.checked = true;
        });
        
        const initialValue = event.target.closest("input:checked").value;
        const isSize = event.target.classList.contains("product-form__input-size");
        const isProductBar = event.target.classList.contains("is-product-bar");

        if (isSize) {
        switch (value) {
            case "PP":
            value = "Petite Petite";
            break;
            case "P":
            value = "Petite";
            break;
            case "S":
            value = "Small";
            break;
            case "M":
            value = "Medium";
            break;
            case "L":
            value = "Large";
            break;
            case "V":
            value = "Voluptuous";
            break;
            case "VV":
            value = "Very Voluptuous";
            break;
            case "VVV":
            value = "Very Very Voluptuous";
            break;
            case "OS":
            value = "One Size";
            case "O/S":
            value = "One Size";
            break;
            case "P/S":
            value = "Petite / Small";
            break;
            case "S/M":
            value = "Small / Medium";
            break;
            case "M/L":
            value = "Medium / Large";
            break;
            case "L/V":
            value = "Large / Voluptuous";
            break;
            case "S/L":
            value = "Small Band Large Cup";
            break;

            default:
            value;
        }

        if (inventory > 0 && inventory <= 10) {
            document.getElementById("almost-sold-out").style.display = "block";
        } else {
            document.getElementById("almost-sold-out").style.display = "none";
        }
        }

        // if (isProductBar) {}
        if(value) {
        value =
            `<span class="product-form__input-active-size-bubble">${initialValue}</span> ` +
            value;
        }
        
        const activeTexts = document.querySelectorAll('.product-form__input .product-form__input-active');
        if(activeTexts.length > 0){
            activeTexts.forEach((activeText) => {
                activeText.innerHTML = value;
            });
        }

        const productBar = document.querySelector('.product-bar');
        if (productBar) {
        productBar.querySelector('.product-form__input-active').innerHTML = value;
        }
    }

    updatePickupAvailability() {
        const pickUpAvailability = document.querySelector("pickup-availability");
        if (!pickUpAvailability) return;

        if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
        pickUpAvailability.removeAttribute("available");
        pickUpAvailability.innerHTML = "";
        }
    }

    removeErrorMessage() {
        const section = this.closest("section");
        if (!section) return;

        const productForm = section.querySelector("product-form");
        if (productForm) productForm.handleErrorMessage();
    }

    renderProductInfo() {
        fetch(
        `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
        )
        .then((response) => response.text())
        .then((responseText) => {
            const id = `price-${this.dataset.section}`;
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const destination = document.getElementById(id);
            const source = html.getElementById(id);

            if (source && destination) destination.innerHTML = source.innerHTML;

            const price = document.getElementById(`price-${this.dataset.section}`);

            if (price) price.classList.remove("visibility-hidden");
            this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings.soldOut
            );
        });
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(
        `product-form-${this.dataset.section}`
        );
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');

        if (!addButton) return;

        if (disable) {
        addButton.setAttribute("disabled", "disabled");
        if (text) addButtonText.textContent = text;
        } else {

        const preorderMap = JSON.parse(document.getElementById('preorder-map').textContent);
        const preorder = preorderMap[this.currentVariant.id] === true;

        const inStock = this.currentVariant.inventory_quantity > 0;
        const continueSelling = this.currentVariant.inventory_policy === 'continue';

        let label = '';
        let disabled = false;

        if (preorder) {
            if (inStock || continueSelling) {
            label = 'PREORDER';
            disabled = false;
            } else {
            label = window.variantStrings.soldOut || 'SOLD OUT';
            disabled = true;
            }
        } else {
            if (this.currentVariant.available) {
            label = window.variantStrings.addToCart;
            disabled = false;
            } else {
            label = window.variantStrings.soldOut || 'SOLD OUT';
            disabled = true;
            }
        }

        if (disabled) {
            addButton.setAttribute("disabled", "disabled");
        } else {
            addButton.removeAttribute("disabled");
        }
        addButtonText.textContent = label;
        addButton.removeAttribute("disabled");
        }

        if (!modifyClass) return;
    }

    setUnavailable() {
        const button = document.getElementById(
        `product-form-${this.dataset.section}`
        );
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        if (!addButton) return;
        addButtonText.textContent = window.variantStrings.unavailable;
        if (price) price.classList.add("visibility-hidden");
    }

    getVariantData() {
        this.variantData =
        this.variantData ||
        JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
    }

    customFormButton() {
        let productBarForms = document.querySelectorAll(`[id^="product-form"]`);

        productBarForms.forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event("change", { bubbles: true }));

            if (!productForm) return;
            const addButton = productForm.querySelector('[name="add"]');
            const addButtonText = productForm.querySelector('[name="add"] > span');
            if (!addButton) return;

            // Preorder logic - reading from JSON data or data-preorder attribute

            console.log("this.currentVariant", this.currentVariant)

            const preorderMap = JSON.parse(document.getElementById('preorder-map').textContent);
            console.log("preorderMap", preorderMap)
            const preorder = preorderMap[this.currentVariant.id] === true;


            console.log("preorder", preorder)

            const inStock = this.currentVariant.inventory_quantity > 0;
            const continueSelling = this.currentVariant.inventory_policy === 'continue';

            let label = '';
            let disabled = false;

            if (preorder) {
            if (inStock || continueSelling) {
                label = 'PREORDER';
                disabled = false;
            } else {
                label = window.variantStrings.soldOut || 'SOLD OUT';
                disabled = true;
            }
            } else {
            if (this.currentVariant.available) {
                label = window.variantStrings.addToCart;
                disabled = false;
            } else {
                label = window.variantStrings.soldOut || 'SOLD OUT';
                disabled = true;
            }
            }

            if (disabled) {
            addButton.setAttribute("disabled", "disabled");
            } else {
            addButton.removeAttribute("disabled");
            }
            addButtonText.textContent = label;
        });
    }
}

customElements.define("variant-selects", VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find(
        (radio) => radio.checked
      ).value;
    });
  }
}

customElements.define("variant-radios", VariantRadios);


document.addEventListener("DOMContentLoaded", function () {
  let inputs = document.getElementsByClassName("product-form__input-size");
  let inventory;

  for (var i = 0, l = inputs.length; i < l; ++i) {
    if (inputs[i].checked) {
      inventory = inputs[i].dataset.inventory;
      break;
    }
  }

  if (inventory > 0 && inventory <= 10) {
    if (document.getElementById("almost-sold-out")){
      document.getElementById("almost-sold-out").style.display = "block";
    }
  } else {
    if (document.getElementById("almost-sold-out")){
      document.getElementById("almost-sold-out").style.display = "none";
    }
  }
});

/**
 * Fast dropdown menu transitions - prevents overlap when switching between nav items.
 * Root cause of the "separated" bug: the old code set inline opacity:0 while the
 * theme's WAAPI hide animation (opacity 1→0, 400 ms) was also running. When the
 * inline style was cleared 10 ms later, WAAPI snapped back to ~0.975 opacity,
 * causing a visible flash of the closing menu alongside the newly opened one.
 *
 * Fix: cancel the WAAPI animation BEFORE removing [open], with no inline-style
 * manipulation at all.
 */
document.addEventListener('DOMContentLoaded', function() {
  const headerNavItems = document.querySelectorAll('.header__primary-nav-item');

  headerNavItems.forEach(item => {
    item.addEventListener('mouseenter', function() {
      headerNavItems.forEach(otherItem => {
        if (otherItem !== item) {
          otherItem.querySelectorAll('details[open]').forEach(detail => {
            // 1. Cancel any running WAAPI opacity animations so they cannot
            //    flash back when the inline style is removed.
            detail.querySelectorAll('.mega-menu, .header__dropdown-menu').forEach(el => {
              el.getAnimations().forEach(anim => anim.cancel());
            });

            // 2. Immediately hide — the browser natively hides non-summary
            //    content as soon as [open] is absent, no inline style needed.
            detail.removeAttribute('open');

            // 3. Keep aria-expanded in sync with the theme's internal state.
            detail.setAttribute('aria-expanded', 'false');
          });
        }
      });
    });
  });
});
